// src/components/Header.js
import React, { useState, useEffect } from 'react'; // Removed useCallback as it's not strictly needed here
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate
import {
    AppBar,
    Toolbar,
    ListItemIcon,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Box,
    ListItemText,
    Tooltip,
    Typography,
    Button,
    Select,
    CircularProgress,
    Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// --- Import context and theme hooks ---
import { useChatbots } from '../contexts/ChatbotContext';
import { useTheme } from './../ThemeContext';

import { CUSTOMER_APPLICATION_URL } from "../config";

export default function Header({ toggleSidebar }) {
    // --- STATE MANAGEMENT FROM CONTEXT ---
    const {
        chatbots,
        activeChatbot,
        isLoading,
        switchActiveChatbot
    } = useChatbots();

    // --- LOCAL STATE FOR UI ELEMENTS ---
    const [anchorEl, setAnchorEl] = useState(null);
    const [chatbotMenuAnchor, setChatbotMenuAnchor] = useState(null);
    const { mode, toggleTheme } = useTheme();
    const [customerPortalUrl, setCustomerPortalUrl] = useState('');
    const [fname, setFname] = useState('');
    const [lname, setLname] = useState('');
    const [profileImg, setProfileImg] = useState('');
    const navigate = useNavigate(); // Get the navigate function

    // --- EFFECT FOR USER-SPECIFIC DATA (PROFILE, ETC.) ---
    useEffect(() => {
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        setProfileImg(user?.profile?.profile_image || '');
        setFname(user?.profile?.fname || '');
        setLname(user?.profile?.lname || '');
        const customerToken = localStorage.getItem('customer_portal_token');
        if (customerToken) {
            setCustomerPortalUrl(`${CUSTOMER_APPLICATION_URL}/admin-authentication/${customerToken}`);
        }
    }, []);

    const handleProfileClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileClose = () => {
        setAnchorEl(null);
    };

    // --- CHATBOT MENU HANDLERS ---
    const handleChatbotMenuOpen = (event) => {
        setChatbotMenuAnchor(event.currentTarget);
    };

    const handleChatbotMenuClose = () => {
        setChatbotMenuAnchor(null);
    };

    const handleChatbotSelect = (chatbotId) => {
        switchActiveChatbot(chatbotId, { navigateToDashboard : true });
        handleChatbotMenuClose();
    };

    const handleCreateNew = async () => {
        handleChatbotMenuClose();
        navigate(`chatbot/create`);
    };

    // The render part of your Header component remains exactly the same as in your request.
    // The only change is the logic within the handleCreateNew function above.
    return (
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar>
                <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={{ mr: 2 }}
                    onClick={toggleSidebar}
                >
                    <MenuIcon />
                </IconButton>

                <Typography variant="h6" component="div">KlonIT</Typography>

                <Box sx={{ flexGrow: 1 }} />

                {/* --- IMPROVED CHATBOT SELECTOR --- */}
                <Box sx={{ mr: 2 }}>
                    {isLoading ? (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                ? 'rgba(255,255,255,0.1)' 
                                : 'rgba(0,0,0,0.05)'
                        }}>
                            <CircularProgress color="inherit" size={16} />
                            <Typography variant="body2" color="inherit">
                                Loading...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Button
                                onClick={handleChatbotMenuOpen}
                                variant="outlined"
                                startIcon={<SmartToyIcon />}
                                endIcon={<ExpandMoreIcon />}
                                sx={{
                                    color: 'inherit',
                                    borderColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(255,255,255,0.3)' 
                                        : 'rgba(0,0,0,0.3)',
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(255,255,255,0.1)' 
                                        : 'rgba(0,0,0,0.05)',
                                    minWidth: 200,
                                    justifyContent: 'space-between',
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.5)' 
                                            : 'rgba(0,0,0,0.5)',
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.15)' 
                                            : 'rgba(0,0,0,0.1)',
                                    }
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    flex: 1
                                }}>
                                    <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                                        ACTIVE CHATBOT
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {activeChatbot ? activeChatbot.chatbot_name : 'Select Chatbot'}
                                    </Typography>
                                </Box>
                            </Button>

                            <Menu
                                anchorEl={chatbotMenuAnchor}
                                open={Boolean(chatbotMenuAnchor)}
                                onClose={handleChatbotMenuClose}
                                PaperProps={{
                                    sx: {
                                        minWidth: 280,
                                        maxHeight: 400,
                                        mt: 1
                                    }
                                }}
                            >
                                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Your Chatbots ({chatbots.length})
                                    </Typography>
                                </Box>

                                {chatbots.length === 0 ? (
                                    <MenuItem disabled>
                                        <Box sx={{ py: 2, textAlign: 'center', width: '100%' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No chatbots found
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ) : (
                                    chatbots.map((bot) => (
                                        <MenuItem
                                            key={bot.id}
                                            onClick={() => handleChatbotSelect(bot.id)}
                                            selected={activeChatbot?.id === bot.id}
                                            sx={{
                                                py: 1.5,
                                                '&.Mui-selected': {
                                                    color: 'primary.contrastText',
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <SmartToyIcon
                                                    fontSize="small"
                                                    color={activeChatbot?.id === bot.id ? 'inherit' : 'action'}
                                                />
                                            </ListItemIcon>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                                    {bot.chatbot_name}
                                                </Typography>
                                                {bot.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {bot.description}
                                                    </Typography>
                                                )}
                                                {activeChatbot?.id === bot.id && (
                                                    <Chip
                                                        label="Active"
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            mt: 0.5,
                                                            fontSize: '0.65rem',
                                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                                            color: 'text.primary'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))
                                )}

                                <Divider sx={{ my: 1 }} />

                                <MenuItem onClick={handleCreateNew} sx={{ py: 1.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <AddCircleOutlineIcon fontSize="small" color="primary" />
                                    </ListItemIcon>
                                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                                        Create New Chatbot
                                    </Typography>
                                </MenuItem>

                                <MenuItem component={Link} to="/my-chatbots" onClick={handleChatbotMenuClose} sx={{ py: 1.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <SettingsIcon fontSize="small" color="action" />
                                    </ListItemIcon>
                                    <Typography variant="body2">
                                        Manage Chatbots
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Box>

                <Tooltip title={mode === 'light' ? "Enable Dark Mode" : "Enable Light Mode"}>
                    <IconButton color="inherit" onClick={toggleTheme}>
                        {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
                    </IconButton>
                </Tooltip>
{/* --- CORRECTED PROFILE SECTION --- */}
                <Tooltip title="Account Settings">
                    <Box
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            borderRadius: '24px',
                            p: 0.5,
                            ml: 1,
                            '&:hover': {
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.1)'
                                    : 'rgba(0, 0, 0, 0.05)'
                            }
                        }}
                        onClick={handleProfileClick}
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                    >
                        <Avatar
                            alt={`${fname} ${lname}`}
                            src={profileImg}
                            sx={{ width: 32, height: 32 }}
                        >
                            {!profileImg && <AccountCircle />}
                        </Avatar>
                        
                        {fname && (
                            <Typography
                                variant="body1"
                                sx={{ 
                                    ml: 1, 
                                    mr: 1,
                                    display: { xs: 'none', sm: 'block' },
                                    fontWeight: 500
                                }}
                            >
                                {fname}
                            </Typography>
                        )}
                    </Box>
                </Tooltip>
                <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleProfileClose}
                >
                    <MenuItem onClick={handleProfileClose} component={Link} to="/user-profile">Profile</MenuItem>
                    <MenuItem onClick={handleProfileClose} component={Link} to="/Billing">Billing</MenuItem>
                    <MenuItem onClick={handleProfileClose} component={Link} to="/Link-accounts">Link Accounts</MenuItem>
                    <MenuItem onClick={handleProfileClose} component={Link} to="/Exception-log">Exception Log</MenuItem>
                    {customerPortalUrl && activeChatbot?.is_process_completed && (
                        <MenuItem component="a" href={customerPortalUrl} target="_blank">Customer Portal</MenuItem>
                    )}
                    <MenuItem onClick={handleProfileClose} component={Link} to="/help">Help Center</MenuItem>
                    <MenuItem onClick={handleProfileClose} component={Link} to="/log-out">Log out</MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
}